// TerminusBadge — marks where the run's path deliberately ends.
//
// Renders two things anchored to the FINAL node of actualPath:
//   1. An end-cap: a second, heavier border ringing the node (double border).
//   2. A small badge hanging below the node, styled by end reason.
//
// Rendered as the top layer of the canvas SVG (after all nodes) so the badge
// is never painted under a stacked sibling node. Appears only at terminal
// simulation status — no layout jumps during playback.

import type { TerminusInfo, TerminusReason } from './runStory.ts';
import type { NodePosition } from './layoutEngine.ts';

interface ReasonStyle {
  /** Foreground + border color. */
  color: string;
  /** Translucent tint layered over the opaque pill base. */
  tint: string;
}

const REASON_STYLES: Record<TerminusReason, ReasonStyle> = {
  // Calm green — the flow reached its declared end.
  'terminal-end': { color: 'var(--green)', tint: 'rgba(74, 222, 128, 0.13)' },
  // Orange, clearly EXPECTED — a declared precondition refusal, not the failure red.
  'declared-stop': { color: '#fb923c', tint: 'rgba(251, 146, 60, 0.15)' },
  // Failure red + hazard-striped end-cap — the run broke off early.
  aborted: { color: 'var(--red)', tint: 'rgba(239, 68, 68, 0.13)' },
  // Neutral — completed, but no [terminal] declaration on the final event.
  'neutral-end': { color: 'var(--text-muted)', tint: 'rgba(148, 163, 184, 0.12)' },
};

/** Elongated-octagon points (STOP-sign silhouette) for a rect at x,y,w,h. */
function octagonPoints(x: number, y: number, w: number, h: number): string {
  const c = 4.5; // corner cut
  return [
    [x + c, y],
    [x + w - c, y],
    [x + w, y + c],
    [x + w, y + h - c],
    [x + w - c, y + h],
    [x + c, y + h],
    [x, y + h - c],
    [x, y + c],
  ]
    .map((p) => p.join(','))
    .join(' ');
}

export interface TerminusBadgeProps {
  terminus: TerminusInfo;
  pos: NodePosition;
}

export default function TerminusBadge({ terminus, pos }: TerminusBadgeProps) {
  const rs = REASON_STYLES[terminus.reason];

  // Node rect geometry (mirrors NodeBox): x = pos.x + 6, w = pos.w.
  const nodeX = pos.x + 6;
  const capX = nodeX - 3;
  const capY = pos.y - 3;
  const capW = pos.w + 6;
  const capH = pos.h + 6;

  // Badge hangs below the end-cap, left-aligned with the node. Clipped to the
  // node width so long reasons can never spill into the next lane column.
  const badgeH = 15;
  const badgeY = capY + capH + 3;
  const estW = Math.round(terminus.badgeText.length * 5.1) + 14;
  const badgeW = Math.min(estW, pos.w);
  const badgeX = nodeX;
  const clipId = `terminus-clip-${terminus.stepIndex}`;
  const isStop = terminus.reason === 'declared-stop';
  const isAborted = terminus.reason === 'aborted';

  return (
    <g
      data-testid="terminus-badge"
      data-terminus-reason={terminus.reason}
      style={{ pointerEvents: 'auto' }}
    >
      <title>{terminus.title}</title>

      {/* ── End-cap: heavier second border around the final node ── */}
      {isAborted ? (
        <>
          {/* Hazard stripes: amber base + offset red dashes alternate */}
          <rect
            x={capX}
            y={capY}
            width={capW}
            height={capH}
            rx={6}
            fill="none"
            stroke="var(--amber)"
            strokeWidth={2}
            strokeOpacity={0.85}
          />
          <rect
            x={capX}
            y={capY}
            width={capW}
            height={capH}
            rx={6}
            fill="none"
            stroke="var(--red)"
            strokeWidth={2}
            strokeDasharray="7,7"
            strokeOpacity={0.95}
          />
        </>
      ) : (
        <rect
          x={capX}
          y={capY}
          width={capW}
          height={capH}
          rx={6}
          fill="none"
          stroke={rs.color}
          strokeWidth={1.5}
          strokeOpacity={terminus.reason === 'neutral-end' ? 0.55 : 0.8}
        />
      )}

      {/* ── Badge below the node ── */}
      {isStop ? (
        <>
          <polygon points={octagonPoints(badgeX, badgeY, badgeW, badgeH)} fill="var(--bg-pill)" />
          <polygon
            points={octagonPoints(badgeX, badgeY, badgeW, badgeH)}
            fill={rs.tint}
            stroke={rs.color}
            strokeWidth={1.2}
            strokeOpacity={0.9}
          />
        </>
      ) : (
        <>
          <rect
            x={badgeX}
            y={badgeY}
            width={badgeW}
            height={badgeH}
            rx={7.5}
            fill="var(--bg-pill)"
          />
          <rect
            x={badgeX}
            y={badgeY}
            width={badgeW}
            height={badgeH}
            rx={7.5}
            fill={rs.tint}
            stroke={rs.color}
            strokeWidth={1}
            strokeOpacity={isAborted ? 0.9 : 0.7}
            strokeDasharray={isAborted ? '4,3' : 'none'}
          />
        </>
      )}
      <clipPath id={clipId}>
        <rect x={badgeX + 4} y={badgeY} width={badgeW - 8} height={badgeH} />
      </clipPath>
      <text
        x={badgeX + 7}
        y={badgeY + badgeH / 2 + 1}
        dominantBaseline="middle"
        fill={rs.color}
        fontSize="8"
        fontWeight={700}
        fontFamily="'JetBrains Mono', monospace"
        letterSpacing="0.02em"
        clipPath={`url(#${clipId})`}
      >
        {terminus.badgeText}
      </text>
    </g>
  );
}
